// VEIL-1 — **THE OWNER'S VEILED GROUP OVERSIGHT, CLIENT SIDE.** Her order, 2026-08-10.
//
// Its own file for the reason every owner surface since D2 has had one: App.tsx is at the R8 ceiling
// and an owner feature pays for its own lines rather than borrowing against them.
//
// FOUR THINGS THIS COMPONENT DELIBERATELY DOES NOT DO, because each is how an oversight screen fails:
//
//   1. **IT NEVER DECIDES WHETHER IT IS UNLOCKED.** `supportDoorView` in `src/supportView.js` derives
//      that from the server's own status answer, and every veil route re-checks the ticket regardless.
//      A screen that decides for itself always drifts the same direction — it thinks it is open.
//   2. **IT NEVER COMPOSES THE ACTION ROSTER.** `actions` comes DOWN with the payload out of
//      `src/veil.js`, so the window cannot offer an act the door does not perform, and a control that
//      wandered onto this screen would have no door to post to (`veilActionRoute` answers `""`).
//   3. **IT HAS NO SEND, NO REPLY AND NO REACTION — BY CONSTRUCTION AND NOT BY HIDING.** There is no
//      composer in this file, no like handler, and no message-delete control; her clause 3 is proved
//      by the VERB, exactly as D3-1's read-only support view is (`scripts/veil-groups.mjs` counts the
//      POSTs in this file and holds every one of them to `veilActionRoute`'s two doors).
//   4. **IT NEVER RENDERS A READ RECEIPT OR A LAST-ONLINE**, because the payload does not carry one.
//      The store's veil reader does not select those columns at all, so there is nothing on the wire
//      for a later round to start drawing (`VEIL_MESSAGE_FORBIDDEN_FIELDS`).
//
// **THE UNLOCK PROMPT IS A SECOND SURFACE AT ONE DOOR, NEVER A SECOND DOOR** (DECISIONS VEIL-1-5).
// It POSTs to `/api/owner/support/unlock` — the same route the support view uses, the same passcode,
// the same ticket — because her clause 2 is *"re-entered per session"* and sending her to a member's
// detail page to unlock before she can answer a bullying complaint is a wall between her and the
// thing she opened the dashboard for.
import { useCallback, useEffect, useState } from "react";
import {
  VEIL_ACTION_ROUTE, VEIL_LIST_ROUTE, MOD_ACTION_ROUTE, veilThreadHref,
  VEIL_EMPTY, VEIL_NO_MATCH, VEIL_WAITING, VEIL_FAILED, VEIL_THREAD_EMPTY, VEIL_THREAD_FAILED,
  VEIL_SEARCH_PLACEHOLDER, VEIL_INTRO, VEIL_READ_ONLY_NOTE, VEIL_LEDGER_NOTE,
  veilListState, veilThreadState, veilActionRoute,
} from "../veil.js";
import { supportDoorView, supportDoorViewCopy, PASSCODE_UNREAD } from "../supportView.js";
import { loadSupportData, supportReadNote } from "../lib/support-read";
// **THE IDENTITY TYPE IS IMPORTED AND NOT RE-DECLARED.** A hand-written copy of it here would drift
// from `publicIdentity`'s real shape the first time that law grows a field, and a copy that has
// dropped one is exactly the subset defect `reading-form.ts` records (R46) — on the one type whose
// whole job is that no surname can ride in it.
import { Avatar, OwnerCrown, type Identity } from "./LivingRoomShared";
type VeilAction = { key: string; label: string; note: string };
type Group = {
  id: string; title: string; named: boolean; slug: string | null; image: string | null;
  creator: string; members: number; messages: number; lastMessageAt: string | null; createdAt: string | null;
};
type VeilMedia = { id: string; kind: string; url: string };
type VeilMessage = {
  id: string; body: string; author: Identity; visibility: string; deleted: boolean;
  media: VeilMedia[]; createdAt: string | null;
};
type VeilThread = {
  id: string; title: string; named: boolean; image: string | null;
  participants: Identity[]; messages: VeilMessage[]; createdAt: string | null; lastMessageAt: string | null;
};
type Status = { passcodeSet: boolean; unlocked: boolean; message?: string };

const stamp = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : "");

export function OwnerVeil({ onDenied }: { onDenied: () => void }) {
  const [status, setStatus] = useState<Status | null>(null);
  const [doorNote, setDoorNote] = useState("");
  const [entry, setEntry] = useState("");
  const [busy, setBusy] = useState(false);

  // FOUR STATES ON THE LIST AND THREE ON THE THREAD, and the decision is `src/veil.js`'s so a gate
  // RUNS it. `null` is a read that has not answered and HOLDS STILL; a failed read says so in its own
  // sentence. Telling her the site has no groups because a query was slow is the one wrong thing to
  // say on the screen she opens to answer a complaint.
  const [groups, setGroups] = useState<Group[] | null>(null);
  const [listFailed, setListFailed] = useState(false);
  const [actions, setActions] = useState<VeilAction[]>([]);
  const [q, setQ] = useState("");
  const [thread, setThread] = useState<VeilThread | null>(null);
  const [threadFailed, setThreadFailed] = useState(false);
  const [openId, setOpenId] = useState("");
  const [act, setAct] = useState("");

  const readStatus = useCallback(async () => {
    const answer = await loadSupportData<Status>("/api/owner/support/status");
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    // The door is only ever set from an answer that LANDED (R78's lesson one surface over): a failed
    // read leaves it unknown rather than drawing the first-time setup form over a dropped request.
    if (answer.ok) setStatus({ passcodeSet: answer.data.passcodeSet, unlocked: answer.data.unlocked, message: answer.data.message });
    setDoorNote(supportReadNote(answer, PASSCODE_UNREAD));
  }, [onDenied]);

  useEffect(() => { void readStatus(); }, [readStatus]);

  const state = supportDoorView(status);
  const copy = supportDoorViewCopy(status);

  const loadGroups = useCallback(async (term: string) => {
    setListFailed(false);
    const answer = await loadSupportData<{ groups: Group[]; actions: VeilAction[] }>(
      `${VEIL_LIST_ROUTE}${term ? `?q=${encodeURIComponent(term)}` : ""}`);
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    if (!answer.ok && answer.kind === "locked") { setStatus(null); void readStatus(); return; }
    if (answer.ok) { setGroups(answer.data.groups || []); setActions(answer.data.actions || []); return; }
    setListFailed(true);
  }, [onDenied, readStatus]);

  // The list is only ever read through the OPEN door. Asking while locked would put a refusal note on
  // screen under a passcode form, which reads as the passcode having failed.
  //
  // **AND THE SEARCH IS DEBOUNCED, WHICH THE WALKTHROUGH MEASURED THE COST OF.** This route walks
  // every thread on the site; the first build re-ran it on every keystroke, which is a site-wide
  // query per character and — while the list was still ledgered — a row per character in an
  // append-only table (DECISIONS VEIL-1-6).
  useEffect(() => {
    if (state !== "open") return undefined;
    const t = setTimeout(() => { void loadGroups(q); }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [state, q, loadGroups]);

  async function unlock() {
    setBusy(true);
    try {
      const res = await fetch("/api/owner/support/unlock", {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ passcode: entry }),
      });
      const d = await res.json().catch(() => null);
      // A wrong passcode and a stranger both answer 403; they are told apart by WHICH refusal came
      // back. "Not found." is requireOwner's one answer for anyone who is not her.
      if (res.status === 403 && d?.message === "Not found.") { onDenied(); return; }
      if (d?.ok) { setEntry(""); setDoorNote(""); void readStatus(); }
      else setDoorNote(d?.message || "That could not be checked.");
    } catch { setDoorNote("That could not be checked."); }
    finally { setBusy(false); }
  }

  async function openGroup(id: string) {
    setOpenId(id); setThread(null); setThreadFailed(false); setAct("");
    const answer = await loadSupportData<{ thread: VeilThread }>(veilThreadHref(id));
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    if (!answer.ok && answer.kind === "locked") { setOpenId(""); setStatus(null); void readStatus(); return; }
    if (answer.ok && answer.data.thread) { setThread(answer.data.thread); return; }
    setThreadFailed(true);
  }

  // HER CLAUSE 5. **THE DESTINATION IS `veilActionRoute`'s AND NEVER A LITERAL HERE**, so `warn` runs
  // the ladder that has issued every strike on this site since LR1-5 and the two non-ladder acts go
  // to the veil's own door. An unrostered key resolves to no door at all and nothing is sent.
  async function runAction(key: string, message: VeilMessage) {
    const route = veilActionRoute(key);
    if (!route) return;
    setBusy(true); setAct("");
    try {
      const body = route === MOD_ACTION_ROUTE
        ? { action: key, itemKind: "message", id: message.id, authorName: message.author.name, reason: "purpose" }
        : { action: key, messageId: message.id };
      const res = await fetch(route, {
        method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => null);
      if (res.status === 403 && d?.message === "Not found.") { onDenied(); return; }
      setAct(d?.sentence || d?.message || (d?.ok ? "Done." : "That could not be done."));
    } catch { setAct("That could not be done."); }
    finally { setBusy(false); }
  }

  if (state !== "open") {
    return (
      <div className="owner-veil" data-veil-state={state}>
        <h3 className="owner-detail__sub">{copy.heading}</h3>
        <p className="owner-page__note">{copy.message}</p>
        {doorNote ? <p className="owner-page__note owner-fresh--failed" role="status">{doorNote}</p> : null}
        {state === "locked" ? (
          <div className="owner-veil__unlock">
            <input className="owner-search owner-search--tiny" type="password" value={entry} placeholder="Support passcode"
              onChange={(e) => setEntry(e.target.value)} data-veil-passcode />
            <button type="button" className="owner-btn" disabled={busy || !entry} onClick={() => void unlock()}>
              {copy.action}
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  if (openId) {
    const tState = veilThreadState({ thread, failed: threadFailed });
    return (
      <div className="owner-veil owner-veil--thread" data-veil-thread-state={tState}>
        <div className="owner-bar">
          <button type="button" className="owner-link" onClick={() => { setOpenId(""); setThread(null); setThreadFailed(false); }}>
            Back to the groups
          </button>
        </div>
        <h3 className="owner-detail__sub">{thread?.title || "Group"}</h3>
        <p className="owner-page__note owner-veil__readonly">{VEIL_READ_ONLY_NOTE}</p>
        {act ? <p className="owner-page__note" role="status">{act}</p> : null}
        {tState === "waiting" ? <p className="owner-page__note">{VEIL_WAITING}</p> : null}
        {tState === "failed" ? <p className="owner-page__note owner-fresh--failed" role="status">{VEIL_THREAD_FAILED}</p> : null}
        {tState === "empty" ? <p className="owner-page__note">{VEIL_THREAD_EMPTY}</p> : null}
        {tState === "thread" ? (
          <ul className="owner-veil__log">
            {(thread?.messages || []).map((m) => (
              <li key={m.id} className="owner-veil__msg" data-veil-message={m.id} data-veil-visibility={m.visibility}>
                <span className="owner-veil__who">
                  <Avatar who={m.author} size="xs" />
                  <span className="owner-veil__name">@{m.author.name}<OwnerCrown who={m.author} /></span>
                  <span className="owner-veil__when">{stamp(m.createdAt)}</span>
                </span>
                <p className="owner-veil__body">{m.body}</p>
                {m.media.length ? (
                  <span className="owner-veil__media">{m.media.length === 1 ? "1 attachment" : `${m.media.length} attachments`}</span>
                ) : null}
                <span className="owner-veil__acts">
                  {actions.map((a) => (
                    <button key={a.key} type="button" className="owner-link" title={a.note} disabled={busy}
                      data-veil-act={a.key} onClick={() => void runAction(a.key, m)}>{a.label}</button>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }

  const matches = groups ? groups.length : 0;
  const listState = veilListState({ items: groups, failed: listFailed, matches });
  return (
    <div className="owner-veil" data-veil-state={state} data-veil-list-state={listState}>
      <p className="owner-page__note">{VEIL_INTRO}</p>
      <p className="owner-page__note owner-veil__ledger">{VEIL_LEDGER_NOTE}</p>
      <div className="owner-bar">
        <input className="owner-search owner-search--tiny" type="search" value={q} placeholder={VEIL_SEARCH_PLACEHOLDER}
          onChange={(e) => setQ(e.target.value)} data-veil-search />
      </div>
      {listState === "waiting" ? <p className="owner-page__note">{VEIL_WAITING}</p> : null}
      {listState === "failed" ? <p className="owner-page__note owner-fresh--failed" role="status">{VEIL_FAILED}</p> : null}
      {listState === "empty" ? <p className="owner-page__note">{VEIL_EMPTY}</p> : null}
      {listState === "no-match" ? <p className="owner-page__note">{VEIL_NO_MATCH}</p> : null}
      {listState === "list" ? (
        <ul className="owner-veil__groups">
          {(groups || []).map((g) => (
            <li key={g.id} className="owner-veil__group" data-veil-group={g.id}>
              <button type="button" className="owner-veil__open" onClick={() => void openGroup(g.id)}>
                {g.image
                  ? <img className="owner-veil__pic" src={g.image} alt="" />
                  : <span className="owner-veil__pic owner-veil__pic--none" aria-hidden="true" />}
                <span className="owner-veil__meta">
                  <span className="owner-veil__title">{g.title || "Untitled group"}</span>
                  <span className="owner-veil__sub">
                    {g.creator ? `@${g.creator} · ` : ""}{g.members} {g.members === 1 ? "member" : "members"}
                    {" · "}{g.messages} {g.messages === 1 ? "message" : "messages"}
                    {g.lastMessageAt ? ` · ${stamp(g.lastMessageAt)}` : ""}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
